import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';
import validator from 'validator';
import CheckBox from '@material-ui/core/Checkbox';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import {
  Divider,
  injectIntl,
  SmartDialog,
  SmartForm,
} from 'components';

import AutoFill from './AutoFill';

const getTeamPermissionId = ({ permissions } = this.props) => (
  permissions.length ? permissions.find(({ name }) => name === 'Members' || name === 'Team Members').id : null
);
const getProfilePermissionId = ({ permissions } = this.props) => (
  permissions.length ? permissions.find(({ name }) => name === 'Settings').id : null
);

class OrganizationMemberDialog extends Component {
  static contextTypes = {
    currentMember: PropTypes.shape({}),
    currentUser: PropTypes.shape({}),
    isDistributor: PropTypes.bool,
    isFarmer: PropTypes.bool,
    isRestaurant: PropTypes.bool,
  };

  static propTypes = {
    info: PropTypes.shape({
      mPermissions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
      })),
    }),
    isEdit: PropTypes.bool,
    open: PropTypes.bool.isRequired,
    permissions: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
    })),
    submitButtonTitle: PropTypes.string,
    title: PropTypes.string.isRequired,

    createItem: PropTypes.func.isRequired,
    formatMessage: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    updateItem: PropTypes.func.isRequired,
  };

  static defaultProps = {
    info: {},
    isEdit: false,
    permissions: [],
    submitButtonTitle: undefined,
  }

  constructor(props, context) {
    super(props);
    const { isDistributor, isFarmer } = context;

    this.filterOption = 'inBuyer';
    if (isFarmer) {
      this.filterOption = 'inSeller';
    } else if (isDistributor) {
      this.filterOption = 'inDistributor';
    }
    this.businessType = this.filterOption.substr(2);

    this.state = {
      currentPermissions: this.getCurrentPermissions(props),
      currentRole: null,
      info: props.info,
      isOrganization: false, // flag that indicates the currently searched account is an Org,
      refreshCount: 0,
    };
  }

  onSave = (data) => {
    const { createItem, updateItem, isEdit } = this.props;
    const { currentPermissions, currentRole, info } = this.state;
    const payload = {
      ...data,
      email: info.email,
      mPermissions: currentPermissions,
      role: currentRole,
    };
    if (isEdit) {
      updateItem(payload);
    } else {
      createItem(payload);
    }
    this.handleClose();
  };

  getCurrentPermissions = ({ info = {} }, isOrganization) => {
    const { permissions } = this.props;
    const { mPermissions } = info;

    if (mPermissions && mPermissions.length) return mPermissions;

    if (isOrganization !== undefined) {
      return permissions
        .filter(permission =>
          get(permission, ['visibilityMap', this.businessType, this.getMemberType(isOrganization), 'checked'], false))
        .map(permission => ({
          permissionId: permission.id,
          writeAccess: permission.id !== getProfilePermissionId(this.props),
        }));
    }

    return [];
  };

  getDisabled = permission =>
    !get(permission, ['visibilityMap', this.businessType, this.getMemberType(), 'enabled'], false);

  getMemberType = (isOrganization = this.state.isOrganization) => (isOrganization ? 'org' : 'personal');

  editableSet = (permissionId, editable) => {
    const permissions = this.state.currentPermissions.slice();
    const currentPermission = permissions.find(
      permission => permission.permissionId === permissionId,
    );
    currentPermission.writeAccess = editable;
    this.setState({ currentPermissions: permissions });
  };

  handleChangeRole = (e, role) => {
    const { isFarmer, isDistributor } = this.context;
    const { permissions } = this.props;
    let currentPermissions = [];

    const teamPermissionId = getTeamPermissionId(this.props);
    const profilePermissionId = getProfilePermissionId(this.props);

    let personal = true;
    if (role === 'admin') {
      currentPermissions = permissions
        .filter(({ inBuyer, inDistributor, inSeller, isDivider }) =>
          !isDivider &&
          (isFarmer ? inSeller : isDistributor ? inDistributor : inBuyer),
        );
    } else if (role === 'team-member') {
      currentPermissions = permissions
        .filter(({ inBuyer, inDistributor, inSeller, forAdmin, isDivider }) =>
          !isDivider &&
          !forAdmin &&
          (isFarmer ? inSeller : isDistributor ? inDistributor : inBuyer),
        );
    } else if (role === 'store-manager' || role === 'order-manager') {
      currentPermissions = this.getCurrentPermissions(this.props, true);
      personal = false;
    }
    if (personal) {
      currentPermissions = currentPermissions.map(({ id }) => ({
        permissionId: id,
        writeAccess: true, // teamPermissionId !== id && profilePermissionId !== id,
      }));
    }

    this.setState({ currentPermissions, currentRole: role });
  };

  handleSelect = (info) => {
    const isOrganization = Boolean(info.role || info.currentRoleId
      || (info.profile && info.profile.isOrganization));

    this.setState({
      currentPermissions: this.getCurrentPermissions({ info }),
      currentRole: info.permissionRole || null,
      info: {
        ...info,
        selected: true,
      },
      isOrganization,
      refreshCount: this.state.refreshCount + 1,
    });
  }

  handleClose = () => {
    this.setState({
      currentPermissions: [],
      currentRole: null,
      info: {},
      isOrganization: false,
      refreshCount: 0,
    });
    this.props.onClose();
  };

  showPermission = (permissionId, show) => {
    let permissions = this.state.currentPermissions.slice();
    const currentPermission = permissions.find(
      permission => permission.permissionId === permissionId,
    );
    if (show) {
      if (currentPermission) {
        currentPermission.show = true;
      } else {
        permissions.push({
          permissionId,
          writeAccess: permissionId !== getProfilePermissionId(this.props),
        });
      }
    } else if (currentPermission) {
      permissions = permissions.filter(
        permission => permission.permissionId !== currentPermission.permissionId,
      );
    }
    this.setState({ currentPermissions: permissions });
  };

  render() {
    const {
      currentMember,
      currentUser,
      isFarmer,
      isDistributor,
      isRestaurant,
    } = this.context;
    const {
      isEdit,
      open,
      permissions,
      submitButtonTitle,
      title,
      formatMessage,
    } = this.props;
    const { currentPermissions, info, isOrganization, currentRole, refreshCount } = this.state;
    const details = info;
    const userEmail = (currentMember && currentMember.email) || currentUser.account.email;
    const isMe = info.email && info.email === userEmail;
    const isGuest = !info.profile;
    const isSelected = info.selected || validator.isEmail(info.email || '');
    const isMemberDistributor = Number(get(info, 'role.id') || get(info, 'currentRoleId')) === 3;
    const isMemberRestaurant = Number(get(info, 'role.id') || get(info, 'currentRoleId')) === 2;

    let warningLabel = '';
    let warningText = '';
    if (isMe) {
      warningLabel = formatMessage('YOU CANNOT INVITE YOURSELF.');
    } else if (isSelected && !isEdit) {
      warningLabel = formatMessage('WARNING:');
      if (isGuest) {
        warningLabel = formatMessage('THIS EMAIL IS NOT YET REGISTERED ON FRESHR.');
      }
      if (isOrganization) { // the invited user is an ORG
        // warningText = formatMessage('Email is the Owner of another Organization on FRESHr
        // and might not be an employee of your organization. By inviting them you agree to
        // take full responsibility for any actions taken on behalf of your organization.');
        if (isDistributor) { // I am a distributor
          warningText = formatMessage('Distributors cannot add another organization as a member.');
        } else if (isFarmer && !isMemberDistributor) {
          warningText = formatMessage('Sellers cannot add another Seller or Buyer organization as a member.');
        } else if (isRestaurant && isMemberRestaurant) {
          warningText = formatMessage('Buyers cannot add another Buyer as a member.');
        } else {
          warningText = formatMessage('{companyName} is a Business Account. By inviting them to join your Business Account you agree to be responsible for all actions taken by them on behalf of your Business.', { companyName: details.profile.companyName });
        }
      } else if (!isGuest) {
        warningText = formatMessage('{firstName} {lastName} is an individual who might not be your employee. By adding them as a member of your Business Account you agree to take full responsibility for this individual\'s actions on behalf of your Business.', { firstName: info.profile.firstName, lastName: info.profile.lastName });
      }
    }

    const submitDisabled = isEmpty(currentPermissions) || isEmpty(info) || isMe || !isSelected;

    const profile = details.profile || {};

    if (profile) {
      details.firstName = profile.firstName;
      details.lastName = profile.lastName;
    }

    const fields = [
      {
        fieldName: 'firstName',
        kind: 'input',
        label: formatMessage('First Name'),
        name: 'firstName',
        readOnly: Boolean(details.firstName),
      },
      {
        fieldName: 'lastName',
        kind: 'input',
        label: formatMessage('Last Name'),
        name: 'lastName',
        readOnly: Boolean(details.lastName),
      },
    ];

    return (
      <SmartDialog
        noOutsideClick
        onClose={this.handleClose}
        open={open}
        title={title}
        closeButtonOnTop
      >
        <div className="organization-member-dialog">
          <AutoFill
            clearInfo={email => this.setState({
              info: { email, selected: false },
              refreshCount: refreshCount + 1,
            })}
            email={info.email}
            info={info}
            onSelect={this.handleSelect}
          />
          <SmartForm
            defaultData={details}
            fields={fields}
            key={refreshCount}
            onCancel={this.handleClose}
            onSave={this.onSave}
            placeChildrenBottom
            saveButton={submitButtonTitle || title}
            saveButtonDisable={submitDisabled}
          >
            <div className="col-12">
              {details.email && warningLabel &&
                <div className="warning-section">
                  <span className="warning-label">{warningLabel}</span>&nbsp;
                  {warningText &&
                    <span className="warning-text">
                      {warningText}
                    </span>
                  }
                </div>
              }
              {isOrganization ? (
                isFarmer && isMemberDistributor ? (
                  <Fragment>
                    <Divider />
                    <FormControlLabel
                      value="store-manaer"
                      control={
                        <CheckBox
                          checked={currentRole === 'store-manager'}
                          onChange={({ target: { checked } }) => this.handleChangeRole({}, checked ? 'store-manager' : '')}
                          value="store-manager"
                          color="primary"
                        />
                      }
                      label={(
                        <div className="d-flex">
                          <div>{formatMessage('Store Manager')}</div>
                          <div style={{ color: 'gray' }} className="ml-3">
                            {formatMessage('Accesses Store Front')}
                          </div>
                        </div>
                      )}
                      labelPlacement="start"
                      className="d-flex justify-content-between mb-0"
                    />
                    <Divider />
                  </Fragment>
                ) : (
                  isRestaurant && !isMemberRestaurant && (
                    <Fragment>
                      <Divider />
                      <FormControlLabel
                        value="order-manager"
                        control={
                          <CheckBox
                            checked={currentRole === 'order-manager'}
                            onChange={({ target: { checked } }) => this.handleChangeRole({}, checked ? 'order-manager' : '')}
                            value="order-manager"
                            color="primary"
                          />
                        }
                        label={(
                          <div className="d-flex">
                            <div>{formatMessage('Order Manager')}</div>
                            <div style={{ color: 'gray' }} className="ml-3">
                              {formatMessage('Access Order')}
                            </div>
                          </div>
                        )}
                        labelPlacement="start"
                        className="d-flex justify-content-between mb-0"
                      />
                      <Divider />
                    </Fragment>
                  )
                )
              ) : !isMe && (
                <RadioGroup
                  aria-label={formatMessage('role')}
                  name="role"
                  value={currentRole}
                  onChange={this.handleChangeRole}
                >
                  <Divider />
                  <FormControlLabel
                    value="admin"
                    control={<Radio color="primary" />}
                    label={(
                      <div className="d-flex">
                        <div>{formatMessage('Admin')}</div>
                        <div style={{ color: 'gray' }} className="ml-3">
                          {formatMessage('Access to all features')}
                        </div>
                      </div>
                    )}
                    labelPlacement="start"
                    className="d-flex justify-content-between mb-0"
                  />
                  <Divider />
                  <FormControlLabel
                    value="team-member"
                    control={<Radio color="primary" />}
                    label={(
                      <div className="d-flex">
                        <div>{formatMessage('Member')}</div>
                        <div style={{ color: 'gray' }} className="ml-3">
                          {permissions
                            .filter(({ forAdmin, inBuyer, inDistributor, inSeller, isDivider }) =>
                              !forAdmin &&
                              !isDivider &&
                              (isFarmer ? inSeller : isDistributor ? inDistributor : inBuyer))
                            .map(({ name }) => formatMessage(name))
                            .join(', ')
                          }
                        </div>
                      </div>
                    )}
                    labelPlacement="start"
                    className="d-flex justify-content-between mb-0"
                  />
                  <Divider />
                </RadioGroup>
              )}
            </div>
          </SmartForm>
        </div>
      </SmartDialog>
    );
  }
}

const wrappedOrganizationMemberDialog = injectIntl(OrganizationMemberDialog);
wrappedOrganizationMemberDialog.displayName = 'OrganizationMemberDialog';

export default wrappedOrganizationMemberDialog;
